import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, MessageSquare, AtSign, Megaphone, CheckCheck } from 'lucide-react';
import api from '../lib/api';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { timeAgo } from '../lib/utils';

const kindMeta = {
  like: { icon: Heart, verb: 'liked your comment' },
  reply: { icon: MessageSquare, verb: 'replied to your comment' },
  comment: { icon: MessageSquare, verb: 'commented' },
  mention: { icon: AtSign, verb: 'mentioned you' },
  broadcast: { icon: Megaphone, verb: 'sent an announcement' },
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/notifications')
      .then(({ data }) => setItems(data.notifications || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // mark all read on open
    api.post('/notifications/mark-read').catch(() => {});
  }, []);

  if (loading)
    return (
      <div className="grid h-[60vh] place-items-center">
        <Spinner className="h-8 w-8" />
      </div>
    );

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Notifications</h1>
          <p className="mt-1 text-sm text-zinc-500">Replies, likes, mentions and announcements.</p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-emerald"><CheckCheck size={14} /> All read</span>
      </div>

      {items.length === 0 ? (
        <div className="grain relative rounded-3xl border border-white/5 bg-ink-card p-16 text-center">
          <Bell className="mx-auto text-zinc-600" />
          <p className="mt-3 font-heading text-lg text-white">You're all caught up</p>
          <p className="mt-1 text-sm text-zinc-500">New activity will show up here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => {
            const meta = kindMeta[n.kind] || { icon: Bell, verb: n.kind };
            const Icon = meta.icon;
            return (
              <Card
                key={n.notification_id}
                className={`flex cursor-pointer items-center gap-4 p-4 transition-colors hover:border-white/20 ${!n.read ? 'border-emerald/30 bg-emerald/[0.04]' : ''}`}
                onClick={() => n.lesson_id && navigate(`/app/lessons/${n.lesson_id}`)}
                data-testid={`notification-${n.notification_id}`}
              >
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ink-elevated text-zinc-300">
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-200">
                    <span className="font-medium text-white">{n.from_user?.name || 'Someone'}</span> {meta.verb}
                  </p>
                  <span className="text-xs text-zinc-600">{timeAgo(n.created_at)}</span>
                </div>
                {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-emerald" />}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
